import { WallIntersectionType } from "building-types/settings/settingsTypesEnum";
import { WallType } from "building-types/walls/wallEnums";
import type { WallMassSystem, WallSystem } from "building-types/walls/wallSytemTypes";
import { ANGLE_EPSILON } from "geom/core";
import type { PlanarGraph } from "geom/planarGraph";
import { planarGraphAdjacentHalfEdge, planarGraphHalfEdgeToLine2 } from "geom/planarGraph";
import { polyLine2EdgeTangent } from "geom/polyLine2";
import { vec2AngleBetweenCW } from "geom/vec2";

interface WallStripEndTypes {
  readonly start: WallIntersectionType | undefined;
  readonly end: WallIntersectionType | undefined;
}

export function wallIntersectionTypesAtStripEnds(
  strips: number[][],
  planarGraph: PlanarGraph,
  wallSystem: WallSystem | WallMassSystem,
): WallStripEndTypes {
  if (strips && strips[0] && strips[0].length > 0) {
    const len = strips[0].length;
    const firstStripHalfEdge = strips[0][0];
    const lastStripHalfEdge = strips[0][len - 1];

    const startHalfEdges = planarGraph.vertexHalfEdges[planarGraph.halfEdges[firstStripHalfEdge * 2]];
    const endHalfEdges = planarGraph.vertexHalfEdges[planarGraph.halfEdges[lastStripHalfEdge * 2 + 1]];

    const start = stripWallIntersectionType(wallSystem, planarGraph, lastStripHalfEdge, endHalfEdges, true);
    const end = stripWallIntersectionType(wallSystem, planarGraph, firstStripHalfEdge, startHalfEdges, false);

    return { start, end };
  }
  return { start: undefined, end: undefined };
}

interface WallAngleInfo {
  readonly angle: number;
  readonly halfEdge: number;
}

function stripWallIntersectionType(
  wallSystem: WallSystem | WallMassSystem,
  planarGraph: PlanarGraph,
  halfEdge: number,
  halfEdges: number[],
  isStart: boolean,
): WallIntersectionType {
  const testEdge = isStart ? planarGraphAdjacentHalfEdge(halfEdge) : halfEdge;
  const [x, y] = polyLine2EdgeTangent(planarGraphHalfEdgeToLine2(planarGraph, testEdge), 0);

  const wallAngles: WallAngleInfo[] = [];
  for (const e of halfEdges) {
    if (e === testEdge) {
      continue;
    }
    const wallId = wallSystem.halfEdgeToWall[e];
    const wall = wallSystem.walls[wallId];
    if (wall.wallType === WallType.VOID) {
      continue;
    }

    const [eachX, eachY] = polyLine2EdgeTangent(planarGraphHalfEdgeToLine2(planarGraph, e), 0);
    wallAngles.push({ angle: vec2AngleBetweenCW(x, y, eachX, eachY), halfEdge: e });
  }

  wallAngles.sort((a, b) => a.angle - b.angle);

  if (wallAngles.length === 0) {
    return WallIntersectionType.NONE;
  }

  if (wallAngles.length === 1) {
    return WallIntersectionType.LINE;
  }

  const first = wallAngles[0];
  const last = wallAngles[wallAngles.length - 1];

  if (Math.abs(last.angle - first.angle - Math.PI) < ANGLE_EPSILON) {
    return WallIntersectionType.TEE;
  }

  if (isStart) {
    if (Math.abs(last.angle - 1.5 * Math.PI) < ANGLE_EPSILON) {
      return WallIntersectionType.CORNER;
    } else {
      return WallIntersectionType.CORNER_REVERSE;
    }
  } else {
    if (Math.abs(first.angle - Math.PI) < ANGLE_EPSILON) {
      return WallIntersectionType.CORNER_REVERSE;
    }
  }

  return WallIntersectionType.CORNER;
}
